import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  CalendarDays,
  ChevronDown,
  Info,
  Loader2,
  RefreshCw,
  Users,
} from "lucide-react";
import type { DateRange } from "react-day-picker";
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
} from "date-fns";

import AdminTabActionBar from "@/components/admin_dashboard/AdminTabActionBar";
import PlanFeatureGate from "@/components/admin_dashboard/PlanFeatureGate";
import { Button, buttonVariants } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { STAGE_META, type PipelineStage } from "@/lib/pipelineStage";

interface GeneralAnalyticsProps {
  onNavigateToSection?: (section: string) => void;
}

type TimeFilter = "today" | "week" | "month" | "last7" | "last30" | "custom";

interface CustomerProfileSnapshot {
  contact_id: string;
  preferred_channel: string | null;
  status: string | null;
}

interface ChannelDatum {
  key: string;
  label: string;
  count: number;
  share: number;
  color: string;
}

interface LifecycleDatum {
  key: "new" | "active" | "at-risk" | "dormant" | "lapsed";
  label: string;
  hint: string;
  count: number;
  share: number;
  color: string;
}

const CHANNEL_META: Record<string, { label: string; color: string }> = {
  whatsapp: { label: "WhatsApp", color: "#25D366" },
  instagram: { label: "Instagram", color: "#E4405F" },
  telegram: { label: "Telegram", color: "#229ED9" },
  line: { label: "LINE", color: "#06C755" },
  messenger: { label: "Messenger", color: "#0084FF" },
  email: { label: "Email", color: "#EA4335" },
  sms: { label: "SMS", color: "#F59E0B" },
  phone: { label: "Phone", color: "#6366F1" },
  webchat: { label: "Web chat", color: "#14B8A6" },
  unknown: { label: "Not known", color: "#CBD5E1" },
};

const FALLBACK_CHANNEL_COLORS = ["#8B5CF6", "#EC4899", "#0EA5E9", "#84CC16", "#F97316"];

const LIFECYCLE_COLORS: Record<LifecycleDatum["key"], string> = {
  new: "#0EA5E9",
  active: "#10B981",
  "at-risk": "#F59E0B",
  dormant: "#F97316",
  lapsed: "#F43F5E",
};

const DONUT_RADIUS = 52;
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS;

const FILTER_LABELS: Record<TimeFilter, string> = {
  today: "Today",
  week: "This week",
  month: "This month",
  last7: "Last 7 days",
  last30: "Last 30 days",
  custom: "Custom range",
};

const normalizeChannel = (value: string | null) => {
  if (!value?.trim()) return "unknown";
  const channel = value.trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (channel.includes("whatsapp")) return "whatsapp";
  if (channel.includes("instagram")) return "instagram";
  if (channel.includes("telegram")) return "telegram";
  if (channel === "line" || channel.startsWith("line_")) return "line";
  if (channel.includes("messenger") || channel.includes("facebook")) return "messenger";
  if (channel.includes("email") || channel.includes("gmail")) return "email";
  if (channel.includes("sms") || channel.includes("text")) return "sms";
  if (channel.includes("phone") || channel.includes("call")) return "phone";
  if (channel.includes("web") || channel.includes("chat")) return "webchat";
  return channel;
};

const formatUnknownChannel = (key: string) => key
  .replace(/_/g, " ")
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const normalizeStage = (value: string | null): PipelineStage | null => {
  const stage = String(value ?? "").trim().toLowerCase().replace(/_/g, "-");
  return (["new", "active", "at-risk", "dormant", "lapsed", "vip"] as const)
    .find((candidate) => candidate === stage) ?? null;
};

const MetricLabel = ({ children, description }: { children: string; description: string }) => (
  <span className="inline-flex items-center gap-1.5">
    {children}
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="rounded-full text-muted-foreground/70 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={`About ${children}`}
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[280px] text-xs font-normal leading-relaxed">
        {description}
      </TooltipContent>
    </Tooltip>
  </span>
);

const LifecycleStageLabel = ({ children, description }: { children: string; description: string }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button
        type="button"
        className="cursor-help rounded-sm text-left text-sm font-medium text-muted-foreground decoration-dotted underline-offset-4 hover:text-foreground hover:underline focus-visible:text-foreground focus-visible:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        aria-label={`${children}: ${description}`}
      >
        {children}
      </button>
    </TooltipTrigger>
    <TooltipContent side="top" className="max-w-[280px] text-xs font-normal leading-relaxed">
      {description}
    </TooltipContent>
  </Tooltip>
);

const GeneralAnalytics = (_props: GeneralAnalyticsProps = {}) => {
  const { businessId } = useAuth();
  const [filter, setFilter] = useState<TimeFilter>("month");
  const [customRange, setCustomRange] = useState<DateRange | undefined>();
  const [draftRange, setDraftRange] = useState<DateRange | undefined>();
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [hoveredChannelKey, setHoveredChannelKey] = useState<string | null>(null);

  const getDateRange = useCallback((selectedFilter: TimeFilter): { start: Date; end: Date } => {
    const now = new Date();
    if (selectedFilter === "today") return { start: startOfDay(now), end: endOfDay(now) };
    if (selectedFilter === "week") {
      return {
        start: startOfWeek(now, { weekStartsOn: 1 }),
        end: endOfWeek(now, { weekStartsOn: 1 }),
      };
    }
    if (selectedFilter === "month") return { start: startOfMonth(now), end: endOfMonth(now) };
    if (selectedFilter === "last7") return { start: startOfDay(subDays(now, 6)), end: endOfDay(now) };
    if (selectedFilter === "last30") return { start: startOfDay(subDays(now, 29)), end: endOfDay(now) };
    return {
      start: startOfDay(customRange?.from ?? now),
      end: endOfDay(customRange?.to ?? customRange?.from ?? now),
    };
  }, [customRange]);

  const {
    data: profiles = [],
    isLoading,
    isError,
    isRefetching,
    refetch,
  } = useQuery({
    queryKey: ["customer-intelligence-analytics", businessId],
    queryFn: async (): Promise<CustomerProfileSnapshot[]> => {
      if (!businessId) return [];
      const [contactsResult, profilesResult] = await Promise.all([
        supabase
          .from("contacts")
          .select("contact_id")
          .eq("business_id", businessId)
          .is("tombstoned_at", null)
          // A merged profile lives on inside the one it was merged into, so
          // counting it too would double up (matches the Customers tab).
          .is("merged_into", null),
        supabase
          .from("contact_projections")
          .select("contact_id, preferred_channel, status")
          .eq("business_id", businessId),
      ]);
      if (contactsResult.error) throw contactsResult.error;
      if (profilesResult.error) throw profilesResult.error;

      const liveContactIds = new Set((contactsResult.data ?? []).map((contact) => contact.contact_id));
      return (profilesResult.data ?? []).filter((profile) => liveContactIds.has(profile.contact_id));
    },
    enabled: !!businessId,
    staleTime: 30_000,
  });

  const channelData = useMemo<ChannelDatum[]>(() => {
    const counts = new Map<string, number>();
    for (const profile of profiles) {
      const channel = normalizeChannel(profile.preferred_channel);
      counts.set(channel, (counts.get(channel) ?? 0) + 1);
    }

    return [...counts.entries()]
      .map(([key, count], index) => ({
        key,
        label: CHANNEL_META[key]?.label ?? formatUnknownChannel(key),
        count,
        share: profiles.length > 0 ? (count / profiles.length) * 100 : 0,
        color: CHANNEL_META[key]?.color ?? FALLBACK_CHANNEL_COLORS[index % FALLBACK_CHANNEL_COLORS.length],
      }))
      .sort((a, b) => {
        if (a.key === "unknown") return 1;
        if (b.key === "unknown") return -1;
        return b.count - a.count || a.label.localeCompare(b.label);
      });
  }, [profiles]);

  const { lifecycleData, vipCount, unclassifiedCount } = useMemo(() => {
    const counts: Record<PipelineStage, number> = {
      new: 0,
      active: 0,
      "at-risk": 0,
      dormant: 0,
      lapsed: 0,
      vip: 0,
    };
    let unclassified = 0;

    for (const profile of profiles) {
      const stage = normalizeStage(profile.status);
      if (stage) counts[stage] += 1;
      else unclassified += 1;
    }

    const activeIncludingVip = counts.active + counts.vip;
    const entries: LifecycleDatum[] = [
      {
        key: "new",
        label: STAGE_META.new.label,
        hint: STAGE_META.new.hint,
        count: counts.new,
        share: profiles.length > 0 ? (counts.new / profiles.length) * 100 : 0,
        color: LIFECYCLE_COLORS.new,
      },
      {
        key: "active",
        label: STAGE_META.active.label,
        hint: STAGE_META.active.hint,
        count: activeIncludingVip,
        share: profiles.length > 0 ? (activeIncludingVip / profiles.length) * 100 : 0,
        color: LIFECYCLE_COLORS.active,
      },
      {
        key: "at-risk",
        label: STAGE_META["at-risk"].label,
        hint: STAGE_META["at-risk"].hint,
        count: counts["at-risk"],
        share: profiles.length > 0 ? (counts["at-risk"] / profiles.length) * 100 : 0,
        color: LIFECYCLE_COLORS["at-risk"],
      },
      {
        key: "dormant",
        label: STAGE_META.dormant.label,
        hint: STAGE_META.dormant.hint,
        count: counts.dormant,
        share: profiles.length > 0 ? (counts.dormant / profiles.length) * 100 : 0,
        color: LIFECYCLE_COLORS.dormant,
      },
      {
        key: "lapsed",
        label: STAGE_META.lapsed.label,
        hint: STAGE_META.lapsed.hint,
        count: counts.lapsed,
        share: profiles.length > 0 ? (counts.lapsed / profiles.length) * 100 : 0,
        color: LIFECYCLE_COLORS.lapsed,
      },
    ];

    return { lifecycleData: entries, vipCount: counts.vip, unclassifiedCount: unclassified };
  }, [profiles]);

  const displayedChannel = hoveredChannelKey
    ? channelData.find((channel) => channel.key === hoveredChannelKey) ?? null
    : null;

  const channelSegments = useMemo(() => {
    let cumulativeShare = 0;
    return channelData.map((channel) => {
      const startShare = cumulativeShare;
      cumulativeShare += channel.share;
      return { ...channel, startShare };
    });
  }, [channelData]);

  const actionBar = (
    <div>
      <AdminTabActionBar>
        <Button
          variant="outline"
          size="sm"
          className="admin-toolbar-button gap-2"
          onClick={() => void refetch()}
          disabled={isRefetching}
          title="Refresh data"
          aria-label="Refresh data"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isRefetching ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </AdminTabActionBar>
      <div className="admin-tertiary-bar">
        <Popover
          open={datePickerOpen}
          onOpenChange={(open) => {
            setDatePickerOpen(open);
            if (open) {
              const activeRange = getDateRange(filter);
              setDraftRange({ from: activeRange.start, to: activeRange.end });
            }
          }}
        >
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="admin-toolbar-button justify-start gap-2 font-normal"
              data-testid="analytics-date-range-trigger"
            >
              <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
              <span>{format(getDateRange(filter).start, "d MMM yyyy")} – {format(getDateRange(filter).end, "d MMM yyyy")}</span>
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto overflow-hidden p-0 shadow-lg">
            <div className="flex flex-col sm:flex-row">
              <div
                className="w-[360px] max-w-[calc(100vw-2rem)] overflow-hidden"
                data-testid="analytics-date-range-calendar-panel"
              >
                <div className="border-b px-4 py-3">
                  <p className="text-sm font-medium">
                    {!draftRange?.from ? "Choose a start date" : !draftRange.to ? "Now choose an end date" : "Custom range selected"}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {!draftRange?.from ? "Select the first day of your reporting period." : !draftRange.to ? "Select the last day of your reporting period." : "Review the dates below, then apply the range."}
                  </p>
                </div>
                <Calendar
                  mode="range"
                  numberOfMonths={1}
                  selected={draftRange}
                  onSelect={setDraftRange}
                  modifiers={{
                    analyticsRangeStart: draftRange?.from ? [draftRange.from] : [],
                    analyticsRangeEnd: draftRange?.to ? [draftRange.to] : [],
                    analyticsRangeMiddle: draftRange?.from && draftRange?.to
                      ? [{ after: draftRange.from, before: draftRange.to }]
                      : [],
                  }}
                  modifiersClassNames={{
                    analyticsRangeStart: "admin-date-range-start",
                    analyticsRangeEnd: "admin-date-range-end",
                    analyticsRangeMiddle: "admin-date-range-middle",
                  }}
                  disabled={{ after: new Date() }}
                  defaultMonth={getDateRange(filter).end}
                  className="w-full"
                  classNames={{
                    months: "flex w-full flex-col",
                    month: "w-full min-w-0 space-y-4",
                    nav_button: `${buttonVariants({ variant: "outline" })} h-7 w-7 bg-transparent p-0 opacity-50 hover:!border-input hover:!bg-muted/60 hover:!text-foreground hover:opacity-100`,
                    table: "w-full table-fixed border-collapse",
                    head_row: "grid w-full grid-cols-7",
                    head_cell: "min-w-0 rounded-md text-center text-[0.8rem] font-normal text-muted-foreground",
                    row: "mt-2 grid w-full grid-cols-7",
                    cell: "relative h-9 min-w-0 p-0 text-center text-sm focus-within:relative focus-within:z-20",
                    day: `${buttonVariants({ variant: "ghost" })} admin-date-range-day min-w-0`,
                    day_today: "admin-date-range-today",
                    day_selected: "admin-date-range-selected",
                    day_range_middle: "admin-date-range-middle",
                  }}
                />
                <div className="flex items-center justify-between border-t px-3 py-2">
                  <p className="text-xs text-muted-foreground">
                    {draftRange?.from
                      ? `${format(draftRange.from, "d MMM yyyy")}${draftRange.to ? ` – ${format(draftRange.to, "d MMM yyyy")}` : " – Select end date"}`
                      : "Select a start and end date"}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" className="compact-action-button hover:bg-muted/60 hover:text-foreground" onClick={() => setDatePickerOpen(false)}>Cancel</Button>
                    <Button
                      size="sm"
                      className="compact-action-button"
                      disabled={!draftRange?.from || !draftRange?.to}
                      onClick={() => {
                        if (!draftRange?.from || !draftRange?.to) return;
                        setCustomRange(draftRange);
                        setFilter("custom");
                        setDatePickerOpen(false);
                      }}
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              </div>
              <div className="w-[340px] shrink-0 border-t p-3 sm:border-l sm:border-t-0">
                <p className="px-2 pb-2 pt-1 text-xs font-medium text-muted-foreground">Or select a preset range</p>
                <div className="space-y-0.5">
                  {(["today", "week", "month", "last7", "last30"] as TimeFilter[]).map((preset) => (
                    <Button
                      key={preset}
                      variant={filter === preset ? "secondary" : "ghost"}
                      size="sm"
                      className={filter === preset
                        ? "w-full justify-between border-transparent font-normal hover:border-transparent hover:bg-secondary hover:text-secondary-foreground"
                        : "w-full justify-between font-normal hover:bg-muted/60 hover:text-foreground"}
                      onClick={() => {
                        setFilter(preset);
                        setDatePickerOpen(false);
                      }}
                    >
                      <span>{FILTER_LABELS[preset]}</span>
                      <span className="ml-4 text-[11px] text-muted-foreground">
                        {(() => {
                          const range = getDateRange(preset);
                          if (format(range.start, "yyyy-MM-dd") === format(range.end, "yyyy-MM-dd")) return format(range.start, "d MMM yyyy");
                          if (format(range.start, "MMM yyyy") === format(range.end, "MMM yyyy")) return `${format(range.start, "d")}–${format(range.end, "d MMM yyyy")}`;
                          return `${format(range.start, "d MMM")}–${format(range.end, "d MMM yyyy")}`;
                        })()}
                      </span>
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        {actionBar}
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6">
        {actionBar}
        <Card className="!mt-0 border-border shadow-none" role="alert">
          <CardContent className="flex flex-col items-start gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              <div>
                <h3 className="font-semibold">Customer analytics are temporarily unavailable</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Dibs could not load communication preferences or lifecycle stages, so no partial totals are shown.
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => void refetch()} disabled={isRefetching}>
              <RefreshCw className={`mr-2 h-3.5 w-3.5 ${isRefetching ? "animate-spin" : ""}`} />
              Try again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-2">
        {actionBar}
        <PlanFeatureGate
          feature="analytics_dashboard"
          featureName="Advanced analytics"
          variant="section"
          className="!mt-0 min-h-[420px]"
          contentClassName="space-y-6"
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 3fr) minmax(0, 7fr)",
              gap: "1rem",
            }}
          >
          <Card className="border-border shadow-none" data-testid="preferred-channel-card">
            <CardContent className="p-5 sm:p-6">
              <div>
                <h3 className="font-semibold">Preferred communication channels</h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  Share by preferred channel
                </p>
              </div>

              {profiles.length === 0 ? (
                <div className="mt-6 flex min-h-[240px] flex-col items-center justify-center rounded-xl border border-dashed bg-muted/10 px-6 text-center">
                  <Users className="h-8 w-8 text-muted-foreground/60" />
                  <p className="mt-3 text-sm font-medium">No customer profiles yet</p>
                  <p className="mt-1 max-w-md text-xs text-muted-foreground">
                    Channel shares will appear after customers are imported or start a conversation.
                  </p>
                </div>
              ) : (
                <div className="mt-5 flex min-w-0 items-center gap-4">
                  <svg
                    viewBox="0 0 160 160"
                    width="168"
                    height="168"
                    className="shrink-0"
                    role="img"
                    aria-label={`Preferred communication channels. ${channelData.map((channel) => `${channel.label} ${channel.share.toFixed(0)} percent`).join(", ")}.`}
                    data-testid="preferred-channel-donut"
                    onMouseLeave={() => setHoveredChannelKey(null)}
                  >
                    <circle cx="80" cy="80" r={DONUT_RADIUS} fill="none" stroke="hsl(var(--muted))" strokeWidth="28" />
                    {channelSegments.map((channel) => {
                      const rawLength = (channel.share / 100) * DONUT_CIRCUMFERENCE;
                      const visibleLength = Math.max(rawLength - 3, 1);
                      return (
                        <circle
                          key={channel.key}
                          cx="80"
                          cy="80"
                          r={DONUT_RADIUS}
                          fill="none"
                          stroke={channel.color}
                          strokeWidth="28"
                          strokeDasharray={`${visibleLength} ${DONUT_CIRCUMFERENCE - visibleLength}`}
                          strokeDashoffset={-(channel.startShare / 100) * DONUT_CIRCUMFERENCE}
                          transform="rotate(-90 80 80)"
                          opacity={hoveredChannelKey && hoveredChannelKey !== channel.key ? 0.38 : 1}
                          className="cursor-pointer transition-opacity duration-150 focus:outline-none"
                          data-testid={`channel-segment-${channel.key}`}
                          tabIndex={0}
                          aria-label={`${channel.label}: ${channel.count} customers, ${channel.share.toFixed(1)} percent`}
                          onMouseEnter={() => setHoveredChannelKey(channel.key)}
                          onFocus={() => setHoveredChannelKey(channel.key)}
                          onBlur={() => setHoveredChannelKey(null)}
                        >
                          <title>{channel.label}: {channel.count} customers ({channel.share.toFixed(1)}%)</title>
                        </circle>
                      );
                    })}
                    {displayedChannel && (
                      <g className="pointer-events-none">
                        <text
                          x="80"
                          y="76"
                          textAnchor="middle"
                          className="fill-foreground text-[20px] font-bold"
                          data-testid="channel-pie-center-value"
                        >
                          {displayedChannel.share.toFixed(0)}%
                        </text>
                        <text
                          x="80"
                          y="95"
                          textAnchor="middle"
                          className="fill-muted-foreground text-[9px]"
                          data-testid="channel-pie-center-label"
                        >
                          {displayedChannel.label}
                        </text>
                      </g>
                    )}
                  </svg>

                  <div className="min-w-0 flex-1 space-y-2.5">
                    {channelData.map((channel) => (
                      <div key={channel.key} className="flex min-w-0 items-center gap-2 text-xs">
                        <span className="h-4 w-1 shrink-0 rounded-full" style={{ backgroundColor: channel.color }} />
                        <span className="min-w-0 flex-1 truncate text-muted-foreground">{channel.label}</span>
                        <span className="shrink-0 font-semibold tabular-nums text-foreground">
                          {channel.share.toFixed(0)}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-border shadow-none" data-testid="lifecycle-pipeline-card">
            <CardContent className="p-5 sm:p-6">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                <div>
                  <h3 className="font-semibold">Customer lifecycle pipeline</h3>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Current distribution from new customers through lapsed customers
                  </p>
                </div>
                <MetricLabel description="A current snapshot based on each customer's latest activity. VIP customers are included in Active because they were active recently.">
                  {`${profiles.length} ${profiles.length === 1 ? "customer" : "customers"}`}
                </MetricLabel>
              </div>

              {profiles.length === 0 ? (
                <div className="mt-6 flex min-h-[220px] flex-col items-center justify-center rounded-xl border border-dashed bg-muted/10 px-6 text-center">
                  <Users className="h-8 w-8 text-muted-foreground/60" />
                  <p className="mt-3 text-sm font-medium">No lifecycle data yet</p>
                  <p className="mt-1 max-w-md text-xs text-muted-foreground">
                    The pipeline will populate as Dibs learns when customers joined and last interacted.
                  </p>
                </div>
              ) : (
                <>
                  <div className="mt-6 flex h-12 gap-1 overflow-hidden rounded-lg" aria-label="Lifecycle distribution">
                    {lifecycleData.map((stage) => (
                      stage.count > 0 && (
                        <div
                          key={stage.key}
                          title={`${stage.label}: ${stage.count} (${stage.share.toFixed(1)}%)`}
                          className="flex min-w-[2rem] items-center justify-center text-sm font-semibold tabular-nums text-white"
                          style={{ flexGrow: stage.count, flexBasis: 0, backgroundColor: stage.color }}
                        >
                          {stage.share >= 8 ? stage.count : null}
                        </div>
                      )
                    ))}
                  </div>

                  <div className="mt-6 grid grid-cols-2 gap-y-6 sm:grid-cols-3 lg:grid-cols-5 lg:divide-x">
                    {lifecycleData.map((stage) => (
                      <div key={stage.key} className="min-w-0 px-0 lg:px-5 lg:first:pl-0 lg:last:pr-0">
                        <div className="flex items-center gap-2">
                          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: stage.color }} />
                          <LifecycleStageLabel description={stage.hint}>{stage.label}</LifecycleStageLabel>
                        </div>
                        <p className="mt-2 font-heading text-3xl font-bold tabular-nums tracking-tight">{stage.count}</p>
                        <p className="mt-0.5 text-xs tabular-nums text-muted-foreground">
                          {stage.share.toFixed(1)}% of customers
                        </p>
                      </div>
                    ))}
                  </div>

                  {(vipCount > 0 || unclassifiedCount > 0) && (
                    <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-xs text-muted-foreground">
                      {vipCount > 0 && (
                        <span><strong className="font-medium text-foreground">{vipCount}</strong> VIP {vipCount === 1 ? "customer is" : "customers are"} included in Active.</span>
                      )}
                      {unclassifiedCount > 0 && (
                        <span><strong className="font-medium text-foreground">{unclassifiedCount}</strong> {unclassifiedCount === 1 ? "profile is" : "profiles are"} awaiting lifecycle classification.</span>
                      )}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
          </div>
        </PlanFeatureGate>
      </div>
    </TooltipProvider>
  );
};

export default GeneralAnalytics;
